/**
 * AdminMobileNav
 * ---------------------------------------------------------------
 * Fixed bottom navigation bar + slide-up "More" drawer for the
 * admin panel mobile view. Keeps the 4 most-used tabs instantly
 * reachable; everything else lives one tap away in the drawer.
 */
import React, { useState } from 'react';
import {
  BarChart3, BellRing, LifeBuoy, MapPin, MoreHorizontal, X,
  Users, UserCircle2, ShieldCheck, FileText, Flag, ShieldAlert,
  Send, Megaphone, Sparkles, TrendingUp, CreditCard, Scale,
  Crown, Globe2, Home, LogOut,
} from 'lucide-react';
import { ADMIN_PALETTE, ADMIN_TAB_GROUPS, TAB_COUNT_TONE, STATUS_PALETTE } from './adminDesignSystem';

const PRIMARY_TABS = [
  { id: 'dashboard',  label: 'Overview',  icon: BarChart3 },
  { id: 'approving',  label: 'Approving', icon: BellRing },
  { id: 'support',    label: 'Support',   icon: LifeBuoy },
  { id: 'venue-apps', label: 'Venues',    icon: MapPin },
];

const PRIMARY_IDS = new Set(PRIMARY_TABS.map((t) => t.id));

export const AdminMobileNav = ({
  activeTab,
  setActiveTab,
  tabCounts,
  isMainAdmin,
  onLogout,
  onViewApp,
  onViewMaps,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleTabTap = (tabId) => {
    setActiveTab(tabId);
    setDrawerOpen(false);
  };

  return (
    <>
      {/* Backdrop */}
      {drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          data-testid="admin-mobile-drawer-backdrop"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,17,21,0.55)',
            zIndex: 9000,
          }}
        />
      )}

      {/* More drawer — slides up */}
      {drawerOpen && (
        <div
          data-testid="admin-mobile-more-drawer"
          style={{
            position: 'fixed',
            bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))',
            left: 0,
            right: 0,
            zIndex: 9001,
            background: '#FFFFFF',
            borderRadius: '22px 22px 0 0',
            boxShadow: '0 -20px 60px rgba(15,17,21,0.22)',
            maxHeight: '72vh',
            overflowY: 'auto',
            padding: '0 16px 24px',
          }}
        >
          {/* Drag handle */}
          <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 16px' }}>
            <div style={{ width: 40, height: 4, borderRadius: 9999, background: '#E5E7EB' }} />
          </div>

          {/* All tab groups (excluding primary tabs) */}
          {ADMIN_TAB_GROUPS
            .filter((g) => !g.mainAdminOnly || isMainAdmin)
            .map((group) => {
              const groupTabs = group.tabs.filter((t) => !PRIMARY_IDS.has(t.id));
              if (!groupTabs.length) return null;
              return (
                <div key={group.id} style={{ marginBottom: 20 }}>
                  <p
                    style={{
                      margin: '0 0 10px',
                      fontSize: 10.5,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                      color: ADMIN_PALETTE.inkMuted,
                    }}
                  >
                    {group.label}
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                    {groupTabs.map((tab) => {
                      const Icon = tab.icon;
                      const isActive = activeTab === tab.id;
                      const count = tabCounts[tab.id] || 0;
                      const tone = TAB_COUNT_TONE[tab.id];
                      const tonePalette = STATUS_PALETTE[tone || 'urgent'];
                      return (
                        <button
                          key={tab.id}
                          onClick={() => handleTabTap(tab.id)}
                          data-testid={`admin-mobile-drawer-tab-${tab.id}`}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 6,
                            padding: '14px 6px 12px',
                            borderRadius: 16,
                            border: `1.5px solid ${isActive ? ADMIN_PALETTE.ink : ADMIN_PALETTE.border}`,
                            background: isActive ? ADMIN_PALETTE.ink : '#FFFFFF',
                            color: isActive ? '#FFFFFF' : ADMIN_PALETTE.inkSoft,
                            cursor: 'pointer',
                            position: 'relative',
                            fontFamily: "'Manrope', sans-serif",
                          }}
                        >
                          {count > 0 && (
                            <span
                              style={{
                                position: 'absolute',
                                top: 7,
                                right: 7,
                                background: tonePalette?.solid || '#DD0608',
                                color: '#FFF',
                                borderRadius: 9999,
                                fontSize: 9.5,
                                fontWeight: 800,
                                minWidth: 16,
                                height: 16,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '0 4px',
                              }}
                            >
                              {count > 99 ? '99+' : count}
                            </span>
                          )}
                          <Icon size={20} strokeWidth={2} />
                          <span style={{ fontSize: 11.5, fontWeight: 600, textAlign: 'center', lineHeight: 1.2 }}>
                            {tab.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

          {/* Quick actions row */}
          <div
            style={{
              marginTop: 8,
              paddingTop: 16,
              borderTop: `1px solid ${ADMIN_PALETTE.border}`,
              display: 'flex',
              gap: 10,
            }}
          >
            <button
              onClick={() => { onViewMaps(); setDrawerOpen(false); }}
              data-testid="admin-mobile-drawer-maps"
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '12px',
                borderRadius: 14,
                border: `1px solid ${ADMIN_PALETTE.border}`,
                background: '#FFFFFF',
                color: ADMIN_PALETTE.inkSoft,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              <Globe2 size={16} /> Maps
            </button>
            <button
              onClick={() => { onViewApp(); setDrawerOpen(false); }}
              data-testid="admin-mobile-drawer-app-view"
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '12px',
                borderRadius: 14,
                border: `1px solid ${ADMIN_PALETTE.border}`,
                background: '#FFFFFF',
                color: ADMIN_PALETTE.inkSoft,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              <Home size={16} /> App view
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-mobile-drawer-logout"
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '12px',
                borderRadius: 14,
                border: `1px solid #FCA5A5`,
                background: '#FEF2F2',
                color: '#DC2626',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 13,
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              <LogOut size={16} /> Log out
            </button>
          </div>
        </div>
      )}

      {/* Bottom navigation bar */}
      <div
        data-testid="admin-mobile-nav"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 8000,
          background: '#FFFFFF',
          borderTop: `1px solid ${ADMIN_PALETTE.border}`,
          display: 'flex',
          alignItems: 'stretch',
          minHeight: 64,
          paddingTop: 4,
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 4px)',
          boxSizing: 'content-box',
          boxShadow: '0 -6px 20px rgba(15,17,21,0.06)',
        }}
      >
        {PRIMARY_TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const count = tabCounts[tab.id] || 0;
          const tone = TAB_COUNT_TONE[tab.id];
          const tonePalette = STATUS_PALETTE[tone || 'urgent'];
          return (
            <button
              key={tab.id}
              onClick={() => handleTabTap(tab.id)}
              data-testid={`admin-mobile-nav-${tab.id}`}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: isActive ? ADMIN_PALETTE.ink : ADMIN_PALETTE.inkMuted,
                position: 'relative',
                padding: '6px 4px',
              }}
            >
              {/* Count badge dot */}
              {count > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: 6,
                    right: 'calc(50% - 16px)',
                    background: tonePalette?.solid || '#DD0608',
                    color: '#FFF',
                    borderRadius: 9999,
                    fontSize: 9,
                    fontWeight: 800,
                    minWidth: 15,
                    height: 15,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 3px',
                    border: '1.5px solid #FFF',
                  }}
                >
                  {count > 99 ? '99+' : count}
                </span>
              )}
              {/* Icon pill */}
              <div
                style={{
                  width: 44,
                  height: 28,
                  borderRadius: 14,
                  background: isActive ? ADMIN_PALETTE.surfaceTinted : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 150ms ease',
                }}
              >
                <Icon size={20} strokeWidth={isActive ? 2.5 : 2} />
              </div>
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: isActive ? 700 : 500,
                  fontFamily: "'Manrope', sans-serif",
                  letterSpacing: 0.1,
                }}
              >
                {tab.label}
              </span>
            </button>
          );
        })}

        {/* More button */}
        <button
          onClick={() => setDrawerOpen(!drawerOpen)}
          data-testid="admin-mobile-nav-more"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: drawerOpen ? ADMIN_PALETTE.ink : ADMIN_PALETTE.inkMuted,
            padding: '6px 4px',
          }}
        >
          <div
            style={{
              width: 44,
              height: 28,
              borderRadius: 14,
              background: drawerOpen ? ADMIN_PALETTE.surfaceTinted : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 150ms ease',
            }}
          >
            {drawerOpen
              ? <X size={20} strokeWidth={2.5} />
              : <MoreHorizontal size={20} strokeWidth={2} />}
          </div>
          <span
            style={{
              fontSize: 10.5,
              fontWeight: drawerOpen ? 700 : 500,
              fontFamily: "'Manrope', sans-serif",
            }}
          >
            More
          </span>
        </button>
      </div>
    </>
  );
};
